import { useEffect, useMemo, useState } from 'react';
import { SectionTabs } from '../../components/ux/SectionTabs';
import {
  listParentLearnerScopes,
  listParentLearnerSubjects,
  loadParentLearnerInsight,
  type ParentLearnerScopeOption,
  type ParentLearnerSubjectOption,
} from '../../features/insights/learnerInsightSupabase';
import type {
  LearnerInsight,
  LearnerSkillInsight,
} from '../../features/insights/learnerInsightTypes';

type ParentTab = 'summary' | 'progress' | 'activity';

type ParentDashboardProps = {
  initialTab?: ParentTab;
};

const parentTabs = [
  { value: 'summary', label: 'Ringkasan', description: 'Gambaran belajar anak' },
  { value: 'progress', label: 'Perkembangan', description: 'Kemampuan yang sedang tumbuh' },
  { value: 'activity', label: 'Aktivitas', description: 'Sesi belajar terbaru' },
];

function formatDateTime(value: string | null | undefined) {
  if (!value) return 'Belum ada aktivitas';
  return new Date(value).toLocaleString('id-ID', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatDurationMs(value: number) {
  const minutes = Math.max(1, Math.round(value / 60000));
  if (minutes < 60) return `${minutes} menit`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `${hours} jam ${remainder} menit` : `${hours} jam`;
}

function ParentSkillGroup(props: {
  title: string;
  description: string;
  skills: LearnerSkillInsight[];
  emptyLabel: string;
}) {
  return (
    <section className="card parent-skill-group">
      <div className="section-heading">
        <div>
          <h2>{props.title}</h2>
          <p className="muted">{props.description}</p>
        </div>
        <span className="availability-pill availability-pill--active">{props.skills.length}</span>
      </div>
      <div className="parent-skill-list">
        {props.skills.map((skill) => (
          <article className="parent-skill-row" key={skill.skillId}>
            <strong>{skill.learnerStatement}</strong>
            <span className="muted">Terakhir belajar: {formatDateTime(skill.lastActivityAt)}</span>
          </article>
        ))}
        {!props.skills.length ? <p className="muted">{props.emptyLabel}</p> : null}
      </div>
    </section>
  );
}

export function ParentDashboard({ initialTab = 'summary' }: ParentDashboardProps) {
  const [scopes, setScopes] = useState<ParentLearnerScopeOption[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [subjects, setSubjects] = useState<ParentLearnerSubjectOption[]>([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [insight, setInsight] = useState<LearnerInsight | null>(null);
  const [activeTab, setActiveTab] = useState<ParentTab>(initialTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshRevision, setRefreshRevision] = useState(0);

  const selectedScope = useMemo(
    () => scopes.find((scope) => scope.studentId === selectedStudentId) ?? scopes[0] ?? null,
    [scopes, selectedStudentId],
  );

  const selectedSubject = useMemo(
    () => subjects.find((subject) => subject.subjectId === selectedSubjectId) ?? subjects[0] ?? null,
    [subjects, selectedSubjectId],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void listParentLearnerScopes()
      .then((rows) => {
        if (cancelled) return;
        setScopes(rows);
        setSelectedStudentId((current) => current || rows[0]?.studentId || '');
        if (!rows.length) setLoading(false);
      })
      .catch((cause) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : 'Data anak belum dapat dimuat.');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedScope) {
      setSubjects([]);
      setSelectedSubjectId('');
      setInsight(null);
      return;
    }
    if (!selectedScope.canViewProgress) {
      setSubjects([]);
      setSelectedSubjectId('');
      setInsight(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    setInsight(null);
    void listParentLearnerSubjects(selectedScope.studentId)
      .then((rows) => {
        if (cancelled) return;
        setSubjects(rows);
        setSelectedSubjectId((current) => (
          rows.some((item) => item.subjectId === current)
            ? current
            : rows[0]?.subjectId || ''
        ));
        if (!rows.length) setLoading(false);
      })
      .catch((cause) => {
        if (!cancelled) {
          setSubjects([]);
          setSelectedSubjectId('');
          setInsight(null);
          setError(cause instanceof Error ? cause.message : 'Perkembangan anak belum dapat dimuat.');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [selectedScope?.studentId, selectedScope?.canViewProgress]);

  useEffect(() => {
    if (!selectedScope?.canViewProgress || !selectedSubject) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    void loadParentLearnerInsight(selectedScope.studentId, selectedSubject.subjectId)
      .then((next) => {
        if (!cancelled) setInsight(next);
      })
      .catch((cause) => {
        if (!cancelled) {
          setInsight(null);
          setError(cause instanceof Error ? cause.message : 'Perkembangan anak belum dapat dimuat.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [
    selectedScope?.studentId,
    selectedScope?.canViewProgress,
    selectedSubject?.subjectId,
    activeTab,
    refreshRevision,
  ]);

  useEffect(() => {
    function refreshWhenVisible() {
      if (document.visibilityState === 'visible') {
        setRefreshRevision((current) => current + 1);
      }
    }
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => document.removeEventListener('visibilitychange', refreshWhenVisible);
  }, []);

  function refresh() {
    setRefreshRevision((current) => current + 1);
  }

  if (!loading && !scopes.length) {
    return (
      <div className="stack parent-workspace">
        <div>
          <span className="page-kicker">PT-REF-01 · ORANG TUA</span>
          <h1>Anak Saya</h1>
        </div>
        <section className="card parent-empty-state">
          <h2>Belum ada anak</h2>
          <p className="muted">Belum ada anak yang terhubung ke akun ini.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="stack parent-workspace">
      <div className="parent-workspace__heading">
        <div>
          <span className="page-kicker">PT-REF-01 · ORANG TUA</span>
          <h1>Anak Saya</h1>
          <p className="muted">Lihat perkembangan belajar dengan bahasa yang ringkas dan mudah dipahami.</p>
        </div>
        <button className="btn parent-refresh-button" type="button" onClick={refresh} disabled={loading}>
          Muat ulang
        </button>
      </div>

      <section className="card parent-scope-card">
        {scopes.length > 1 ? (
          <label className="parent-child-selector" htmlFor="parent-child-selector">
            <span>Anak</span>
            <select
              id="parent-child-selector"
              value={selectedScope?.studentId ?? ''}
              onChange={(event) => {
                setSelectedStudentId(event.target.value);
                setSelectedSubjectId('');
              }}
            >
              {scopes.map((scope) => (
                <option key={scope.studentId} value={scope.studentId}>
                  {scope.studentName}
                </option>
              ))}
            </select>
          </label>
        ) : selectedScope ? (
          <div className="parent-child-identity">
            <span className="muted">Anak</span>
            <strong>{selectedScope.studentName}</strong>
          </div>
        ) : null}

        {selectedScope?.canViewProgress && subjects.length > 1 ? (
          <label className="parent-subject-selector" htmlFor="parent-subject-selector">
            <span>Mata pelajaran</span>
            <select
              id="parent-subject-selector"
              value={selectedSubject?.subjectId ?? ''}
              onChange={(event) => setSelectedSubjectId(event.target.value)}
            >
              {subjects.map((subject) => (
                <option key={subject.subjectId} value={subject.subjectId}>
                  {subject.subjectName}
                </option>
              ))}
            </select>
          </label>
        ) : selectedSubject ? (
          <div className="parent-subject-selector parent-subject-selector--static">
            <span className="muted">Mata pelajaran</span>
            <strong>{selectedSubject.subjectName}</strong>
          </div>
        ) : null}
      </section>

      {selectedScope && !selectedScope.canViewProgress ? (
        <section className="card parent-empty-state">
          <h2>Perkembangan belum tersedia</h2>
          <p className="muted">Akses perkembangan anak belum diaktifkan.</p>
        </section>
      ) : null}

      {selectedScope?.canViewProgress ? (
        <SectionTabs
          value={activeTab}
          tabs={parentTabs}
          ariaLabel="Bagian perkembangan anak"
          onChange={(value) => setActiveTab(value as ParentTab)}
        />
      ) : null}

      {loading ? <section className="card">Menyiapkan perkembangan anak…</section> : null}
      {error ? <div className="feedback feedback--error">{error}</div> : null}

      {!loading && selectedScope?.canViewProgress && !subjects.length && !error ? (
        <section className="card parent-empty-state">
          <h2>Belum ada aktivitas belajar</h2>
          <p className="muted">Belum ada aktivitas belajar. Perkembangan akan muncul setelah latihan pertama.</p>
        </section>
      ) : null}

      {insight && activeTab === 'summary' ? (
        <div className="stack">
          <section className="card parent-summary-card">
            <div className="parent-summary-card__header">
              <div>
                <span className="page-kicker">RINGKASAN</span>
                <h2>{insight.learner.displayName}</h2>
                <p className="muted">{insight.subject.name}</p>
              </div>
              <span className="availability-pill availability-pill--active">
                {insight.currentLevel?.displayName ?? 'Level belum ditetapkan'}
              </span>
            </div>

            <div className="grid grid--3 parent-summary-grid">
              <div className="metric">
                <span>Sedang dipelajari</span>
                <strong>{insight.skillGroups.learning.length}</strong>
              </div>
              <div className="metric">
                <span>Perlu latihan</span>
                <strong>{insight.skillGroups.attention.length}</strong>
              </div>
              <div className="metric">
                <span>Aktivitas terakhir</span>
                <strong className="parent-summary-grid__date">{formatDateTime(insight.lastActivityAt)}</strong>
              </div>
            </div>
          </section>

          {insight.guardianSharedNotes[0] ? (
            <section className="card parent-tutor-note">
              <span className="page-kicker">CATATAN TUTOR</span>
              <h2>Pesan untuk orang tua</h2>
              <p>{insight.guardianSharedNotes[0].body}</p>
              <span className="muted">{formatDateTime(insight.guardianSharedNotes[0].createdAt)}</span>
            </section>
          ) : null}
        </div>
      ) : null}

      {insight && activeTab === 'progress' ? (
        <div className="stack">
          <ParentSkillGroup
            title="Sudah kuat"
            description="Kemampuan yang sudah terlihat mantap dalam proses belajar."
            skills={insight.skillGroups.strong}
            emptyLabel="Belum ada kemampuan yang masuk kelompok ini."
          />
          <ParentSkillGroup
            title="Sedang dipelajari"
            description="Kemampuan yang sedang dibangun melalui latihan."
            skills={insight.skillGroups.learning}
            emptyLabel="Belum ada kemampuan aktif pada kelompok ini."
          />
          <ParentSkillGroup
            title="Perlu latihan"
            description="Kemampuan yang akan lebih baik dengan penguatan atau latihan berikutnya."
            skills={insight.skillGroups.attention}
            emptyLabel="Tidak ada kemampuan yang sedang membutuhkan latihan tambahan."
          />
        </div>
      ) : null}

      {insight && activeTab === 'activity' ? (
        <section className="card parent-activity-card">
          <div className="section-heading">
            <div>
              <span className="page-kicker">AKTIVITAS</span>
              <h2>Belajar terbaru</h2>
            </div>
          </div>
          <div className="parent-activity-list">
            {insight.recentSessions.slice(0, 8).map((session) => (
              <article className="parent-activity-row" key={session.sessionId}>
                <div>
                  <strong>
                    {new Date(session.startedAt).toLocaleDateString('id-ID', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </strong>
                  <span className="muted">
                    {session.answerCount} soal · {session.status === 'completed' ? 'Selesai' : 'Tersimpan'}
                  </span>
                </div>
                <span className="parent-activity-row__duration">{formatDurationMs(session.activeDurationMs)}</span>
              </article>
            ))}
            {!insight.recentSessions.length ? (
              <p className="muted">Belum ada aktivitas belajar. Perkembangan akan muncul setelah latihan pertama.</p>
            ) : null}
          </div>
        </section>
      ) : null}
    </div>
  );
}
